import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import {
    ListItemButton, ListItemText, ListItemSecondaryAction, List
} from '@mui/material';
import { intlShape } from 'react-intl';
import classnames from 'classnames';
import { List as IList } from 'immutable';
import ListItem from '@mui/material/ListItem';
import CheckIcon from '@mui/icons-material/Check';
import { connect } from 'react-redux';
import Alert from '@mui/material/Alert';
import { getCurrencySymbol } from '../intl/getCurrencySymbol';
import {
    getCartCurrencyCode
} from '../productCartHandler/productCartSelectors';
import currencyLocalMessages from './messages/LocaleCurrencyMessages';
import { trackCurrencyLanguageChange, trackCurrencyChange } from '../segment/segmentModule';
import { getIntlLocale } from '../intl/intlSelectors';

const Flag = ({ countryCode, styleNames = {} }) => <i className={classnames(styleNames, 'flag', countryCode)} />;

Flag.defaultProps = {
    styleNames: {}
};

Flag.propTypes = {
    countryCode: PropTypes.string.isRequired,
    styleNames: PropTypes.object
};

const getCurrencyName = (intl, currencyCode) => new Intl.DisplayNames([intl.locale], { type: 'currency' }).of(currencyCode);



const CurrencyPicker = ({
    intl, supportedCurrencyList, selectedCurrency, cartCurrencyCode, setCurrency, popupStatus, selectedLocale, trackCurrencyLanguageAction, location, tempCurrency, setTempCurrency, isSetCurrency, trackCurrencyChangeAction
}) => {
    let newCurrencyList;
    if (tempCurrency !== '') {
        newCurrencyList = React.useMemo(() => IList([tempCurrency, ...supportedCurrencyList.filter(currency => currency !== tempCurrency)]),
            [selectedCurrency, supportedCurrencyList]);
    } else {
        // Memoized currency list to avoid unnecessary recalculations
        newCurrencyList = React.useMemo(() => IList([selectedCurrency, ...supportedCurrencyList.filter(currency => currency !== selectedCurrency)]),
            [selectedCurrency, supportedCurrencyList]);
    }

    const handleCurrencyChange = (newCurrencyCode) => {
        // TODO: remove the below lines once all the currencies are available in sfv2 [phase 2]

        if (!isSetCurrency) {
            if (tempCurrency !== newCurrencyCode) {
                trackCurrencyChangeAction({
                    dropdown_selection: 'currency', current: tempCurrency, new: newCurrencyCode
                });
                setTempCurrency(newCurrencyCode);
                popupStatus(false);
            }
        } else if (selectedCurrency !== newCurrencyCode) {
            setCurrency(newCurrencyCode);
            trackCurrencyLanguageAction({
                currentCurrencyCode: selectedCurrency, nextCurrencyCode: newCurrencyCode, currentLanguage: selectedLocale, nextLanguage: selectedLocale, location
            });
            trackCurrencyChangeAction({
                dropdown_selection: 'currency', current: selectedCurrency, new: newCurrencyCode
            });
            popupStatus(false);
        }
    };

    return (
        <>

            { cartCurrencyCode && (
                <Alert className='cart-alert' sx={{ alignItems: 'center' }} severity='info'>{intl.formatMessage(currencyLocalMessages.changeCurrencyAlertText)}</Alert>
            )}

            <List className='picker-list'>
                {newCurrencyList.map((currency) => {
                    const newCurrencyCode = currency.toUpperCase();
                    return (
                        <Fragment>
                            <ListItem className='list-item' key={newCurrencyCode} disablePadding>
                                <ListItemButton disabled={!cartCurrencyCode ? false : newCurrencyCode !== cartCurrencyCode} className='list-item-button' onClick={() => handleCurrencyChange(newCurrencyCode)}>
                                    <ListItemText
                                        className='list-item-text'
                                        primary={`${newCurrencyCode} - ${getCurrencySymbol(intl, newCurrencyCode).prefix}${getCurrencySymbol(intl, newCurrencyCode).suffix}`}
                                        secondary={getCurrencyName(intl, newCurrencyCode)}
                                    />
                                    { tempCurrency !== ''
                                        ? tempCurrency === newCurrencyCode && (
                                            <ListItemSecondaryAction className='list-item-selected'>
                                                <CheckIcon className='check-icon' />
                                            </ListItemSecondaryAction>
                                        )
                                        : newCurrencyCode === selectedCurrency && (
                                            <ListItemSecondaryAction className='list-item-selected'>
                                                <CheckIcon className='check-icon' />
                                            </ListItemSecondaryAction>
                                        )
                                    }
                                </ListItemButton>
                                <hr />
                            </ListItem>
                        </Fragment>
                    );
                })}
            </List>
        </>
    );
};

CurrencyPicker.defaultProps = {
    popupStatus: () => {},
    setTempCurrency: () => {},
    isSetCurrency: true,
    tempCurrency: ''
};

CurrencyPicker.propTypes = {
    intl: intlShape.isRequired,
    selectedCurrency: PropTypes.string.isRequired,
    supportedCurrencyList: PropTypes.instanceOf(IList).isRequired,
    cartCurrencyCode: PropTypes.string.isRequired,
    setCurrency: PropTypes.func.isRequired,
    popupStatus: PropTypes.func,
    selectedLocale: PropTypes.func.isRequired,
    trackCurrencyLanguageAction: PropTypes.func.isRequired,
    location: PropTypes.string.isRequired,
    setTempCurrency: PropTypes.func,
    isSetCurrency: PropTypes.bool,
    tempCurrency: PropTypes.string,
    trackCurrencyChangeAction: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
    cartCurrencyCode: getCartCurrencyCode(state),
    selectedLocale: getIntlLocale(state)
});

const mapDispatchToProps = {
    trackCurrencyLanguageAction: trackCurrencyLanguageChange,
    trackCurrencyChangeAction: trackCurrencyChange
};

export default connect(mapStateToProps, mapDispatchToProps)(CurrencyPicker);
